import React, { useEffect, useState } from 'react';
import { Loader2, Save, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { COUNTRIES, CountryLabel } from '../portal/countries';
import AdminSisterPanel from './AdminSisterPanel';
import AdminGuidePanel from './AdminGuidePanel';
import AdminForeignPanel from './AdminForeignPanel';
import AdminWalletsPanel from './AdminWalletsPanel';
import AdminProviderCostsPanel from './AdminProviderCostsPanel';
import AdminConsumptionPanel from './AdminConsumptionPanel';
import AdminAmlApi from './AdminAmlApi';
import AdminWb from './AdminWb';
import AdminRequests from './AdminRequests';
import AdminStatements from './AdminStatements';
import AdminSecurityChecks from './AdminSecurityChecks';
import AdminPriceLists from './AdminPriceLists';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (c) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format((c || 0) / 100);
const err = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Errore');

function Prices({ headers }) {
  const [prices, setPrices] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/portal/prices`, { headers }).then((r) => setPrices(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const items = Object.fromEntries(Object.entries(prices).map(([k, v]) => [k, v.cents]));
      setPrices((await axios.put(`${API}/admin/portal/prices`, { items }, { headers })).data);
      toast.success('Listino salvato');
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  if (!prices) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div className="bg-[#0B0D10] border border-[#1F242C] p-6">
      <div className="flex justify-between mb-4"><div><h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Listino prezzi (€)</h3>
        <p className="text-xs text-[#5A5F67] mt-1">Prezzo di ogni servizio addebitato dal plafond e canoni annuali di attivazione delle aree (0 = gratuito). I servizi cyber a preventivo si definiranno in seguito.</p></div>
        <Button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black h-9 rounded-sm" data-testid="admin-prices-save"><Save size={14} className="mr-2" />Salva</Button></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(prices).map(([k, v]) => (
          <label key={k} className="text-sm text-[#A3A3A3]">{v.label}
            <Input type="number" step="0.01" min="0" value={v.cents / 100} data-testid={`admin-price-${k}`}
              onChange={(e) => setPrices({ ...prices, [k]: { ...v, cents: Math.round(Number(e.target.value) * 100) } })}
              className="mt-1 bg-[#050505] border-[#1F242C] text-white font-mono rounded-sm" />
          </label>
        ))}
      </div>
    </div>
  );
}

function Disclaimers({ headers }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/portal/country-disclaimers`, { headers }).then((r) => setItems(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { setItems((await axios.put(`${API}/admin/portal/country-disclaimers`, { items }, { headers })).data); toast.success('Disclaimer salvati'); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  if (!items) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div className="bg-[#0B0D10] border border-[#1F242C] p-6">
      <div className="flex justify-between mb-2"><h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Disclaimer per Paese</h3>
        <Button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black h-9 rounded-sm" data-testid="admin-disclaimers-save"><Save size={14} className="mr-2" />Salva</Button></div>
      <p className="text-xs text-[#5A5F67] mb-4">Il testo compare ai clienti accanto al Paese nella Ricerca aziende. Lascia vuoto per non mostrare nulla.</p>
      <div className="grid lg:grid-cols-2 gap-4">
        {COUNTRIES.map(([c, l]) => (
          <label key={c} className="text-sm text-[#A3A3A3]"><CountryLabel code={c} name={l} />
            <textarea rows={3} value={items[c] || ''} onChange={(e) => setItems({ ...items, [c]: e.target.value })} data-testid={`admin-disclaimer-${c}`}
              className="mt-1 w-full bg-[#050505] border border-[#1F242C] text-white text-sm p-2 rounded-sm focus:outline-none focus:border-[#D4AF37]" />
          </label>
        ))}
      </div>
    </div>
  );
}

const SECTIONS = {
  prices: AdminPriceLists, requests: ({ headers }) => <div className="space-y-6"><AdminRequests headers={headers} /><AdminSecurityChecks headers={headers} /></div>,
  wb: AdminWb, amlapi: AdminAmlApi, statements: AdminStatements, sister: AdminSisterPanel, guide: AdminGuidePanel, foreign: AdminForeignPanel, wallets: AdminWalletsPanel, providers: AdminProviderCostsPanel, consumption: AdminConsumptionPanel,
};

export default function PortalSection({ tab, getHeaders }) {
  const headers = getHeaders();
  const View = SECTIONS[tab] || PortalSettings;
  return <View headers={headers} />;
}

export function PortalSettings({ headers }) {
  const [stats, setStats] = useState(null);
  const [access, setAccess] = useState(null);
  const [mode, setMode] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/portal/stats`, { headers }).then((r) => setStats(r.data));
    axios.get(`${API}/settings/public`).then((r) => setMode(r.data.login_mode || 'portal'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadAccess = () => axios.get(`${API}/admin/portal/creditsafe/access`, { headers }).then((r) => setAccess(r.data)).catch((e) => toast.error(err(e)));
  const saveMode = async (m) => { await axios.put(`${API}/admin/settings`, { login_mode: m }, { headers }); setMode(m); toast.success('Modalità di accesso aggiornata'); };

  return (
    <div className="space-y-6" data-testid="admin-portal-settings">
      <div className="grid sm:grid-cols-4 gap-4">
        {stats && [['Ricariche incassate', eur(stats.topups_cents)], ['N. ricariche', stats.topups_count], ['Report venduti', `${stats.reports_count} · ${eur(stats.reports_cents)}`], ['Plafond residuo clienti', eur(stats.wallet_total_cents)]].map(([l, v]) => (
          <div key={l} className="bg-[#0B0D10] border border-[#1F242C] p-5"><p className="text-xs text-[#5A5F67] uppercase mb-2">{l}</p><p className="font-mono text-xl text-white">{v}</p></div>
        ))}
      </div>
      <div className="bg-[#0B0D10] border border-[#1F242C] p-6 flex flex-wrap items-center gap-4">
        <p className="text-sm text-[#A3A3A3] flex-1">Pulsante "Accedi" del sito: porta al nuovo portale Creditsafe oppure alla versione attuale (EDDI/osintlab).</p>
        {['portal', 'eddi'].map((m) => <Button key={m} onClick={() => saveMode(m)} data-testid={`admin-login-mode-${m}`} className={`h-9 rounded-sm ${mode === m ? 'bg-[#D4AF37] text-black' : 'bg-transparent border border-[#1F242C] text-white'}`}>{m === 'portal' ? 'Portale BOND' : 'EDDI (attuale)'}</Button>)}
      </div>
      <div className="bg-[#0B0D10] border border-[#1F242C] p-6 flex flex-wrap items-center gap-4">
        <p className="text-sm text-[#A3A3A3] flex-1" data-testid="admin-mfa-mandatory">Verifica a due passaggi (MFA) obbligatoria per tutti gli utenti del portale: codice via email oppure app di autenticazione.</p>
      </div>
      <Disclaimers headers={headers} />
      <div className="bg-[#0B0D10] border border-[#1F242C] p-6">
        <div className="flex justify-between mb-4"><h3 className="font-heading text-sm uppercase tracking-widest text-[#BFBFBF]">Crediti contratto Creditsafe</h3><Button onClick={loadAccess} className="h-9 bg-transparent border border-[#1F242C] text-white rounded-sm" data-testid="admin-cs-access"><RefreshCw size={14} className="mr-2" />Verifica</Button></div>
        {access && (access.countryAccess || []).flatMap((g) => Object.entries(g)).filter(([, v]) => v.length).map(([k, v]) => (
          <div key={k} className="mb-3"><p className="text-xs text-[#D4AF37] mb-1">{k}</p>{v.map((x, i) => <p key={i} className="text-sm text-[#A3A3A3] font-mono">{x.templateName || x.name} · usati {x.used}/{x.paid} · scadenza {String(x.expireDate).slice(0, 10)}</p>)}</div>
        ))}
      </div>
    </div>
  );
}
